<template>
  <div style="display: inline">
    <component
      :is="slideoverComponent"
      :title="slideoverTitle"
      :visible="slideoverVisible"
      v-bind="slideoverProperties"
      @toggle-slideover="toggleSlideover"
      @refresh-order="refreshOrder"
    />

    <div class="pb-3 flex flex-row justify-end">
      <ActionsMenu v-if="canShowActions">
        <ActionsItem
          v-if="userCanEditTracking"
          :disabled="!hasSelectedTracking"
          @click="
            toggleSlideover('EditTrackingSlideover', 'Edit Tracking', {
              tracking: selectedTracking,
            })
          "
        >
          Edit Tracking
        </ActionsItem>
        <ActionsItem
          v-if="userCanAddTracking"
          @click="
            toggleSlideover('AddTrackingSlideover', 'Add Freight Tracking')
          "
        >
          Add Freight Tracking
        </ActionsItem>
        <ActionsItem
          v-if="userCanDeleteTracking"
          :disabled="!hasSelectedTracking"
          @click="confirmDeleteTracking"
        >
          Delete Tracking
        </ActionsItem>
      </ActionsMenu>
    </div>

    <ag-grid-vue
      :style="{
        width: '100%',
        height: gridHeight(rowData && rowData.length, 300)
      }"
      class="ag-theme-balham"
      :columnDefs="columnDefs"
      :rowData="rowData"
      :paginationAutoPageSize="false"
      :paginationPageSize="paginationPageSize"
      :pagination="true"
      id="order_tracking"
      :gridOptions="gridOptions"
      @grid-ready="onGridReady"
      :context="context"
      :frameworkComponents="frameworkComponents"
      :defaultColDef="defaultColDef"
      :enableCellTextSelection="true"
      :ensureDomOrder="true"
    >
    </ag-grid-vue>
  </div>
</template>

<script>
import auth from "@/api/auth";
import agGrid from "@/mixins/agGrid";
import { AgGridVue } from "ag-grid-vue";
import { ModalBus } from "@/eventBus.js";
import moneyFormatter from "@/mixins/moneyFormatter";
import { date } from "@/components/agGrid/cellRenderers";
import AddTrackingSlideover from "./slideovers/OrderEdit/AddTracking.vue";
import DeleteTrackingConfirmModal from "./modals/DeleteTrackingConfirm.vue";
import EditTrackingSlideover from "./slideovers/OrderEdit/EditTracking.vue";

export default {
  name: "OrderTrackingTable",
  components: {
    AgGridVue,
    AddTrackingSlideover,
    EditTrackingSlideover,
  },
  filters: {
    currency: (val) => {
      return moneyFormatter.format(val);
    },
  },
  mixins: [agGrid, moneyFormatter],
  data() {
    return {
      selectedTracking: {},
      columnDefs: null,
      rowData: null,
      paginationPageSize: 100,
      frameworkComponents: null,
      gridApi: null,
      columnApi: null,
      gridOptions: null,
      context: null,
      defaultColDef: null,
      showActionsMenu: false,
      overlayLoadingTemplate: true,
      slideoverTitle: "",
      slideoverVisible: false,
      slideoverComponent: null,
      slideoverProperties: {},
    };
  },
  computed: {
    order() {
      return this.$store.getters["orders/get"]("order");
    },
    canShowActions() {
      if (this.order.status?.Status === "Unprocessed") {
        return false;
      }
      return (
        this.userCanAddTracking ||
        this.userCanEditTracking ||
        this.userCanDeleteTracking
      );
    },
    orderWasPlacedOnShopify() {
      return [22, 23, 31, 32].indexOf(Number(this.order.CartID)) > -1;
    },
    orderIsCancelled() {
      return Number(this.order.Cancelled);
    },
    userCanAddTracking() {
      return auth.isUserAuthorized(["admin", "freight_tracking"]);
    },
    userCanEditTracking() {
      return auth.isUserAuthorized(["supervisor", "third-party"]);
    },
    userCanDeleteTracking() {
      return auth.isUserAuthorized(["supervisor", "third-party"]);
    },
    hasSelectedTracking() {
      return Boolean(this.selectedTracking.AutoNumber);
    },
  },
  beforeMount() {
    this.gridOptions = {
      headerHeight: 30,
      rowHeight: 30,
      pagination: false,
      suppressPaginationPanel: true,
      rowSelection: "single",
      rowDeselection: true,
      onSelectionChanged: this.trackingSelectionChanged,
    };

    this.context = { componentParent: this };

    this.defaultColDef = {
      sortable: true,
      filter: true,
      flex: 0,
      suppressMovable: false,
      width: 160,
    };

    this.columnDefs = [
      {
        headerName: "TrackingID",
        field: "TrackingID",
        cellRenderer: (params) => this.orderLink(params),
        width: 150,
      },
      {
        headerName: "Pickup Date",
        valueGetter: (params) => new Date(params.data.PickupDate),
        cellRenderer: "dateCell",
        width: 150,
      },
      {
        headerName: "Facility",
        field: "facility",
        width: 150,
      },
      {
        headerName: "Cost",
        valueGetter: (params) => {
          return moneyFormatter.format(params.data.Cost);
        },
      },
      {
        headerName: "Carrier",
        field: "Carrier",
      },
      {
        headerName: "Box ID",
        field: "BoxID",
      },
      {
        headerName: "L",
        field: "Length",
        width: 50,
      },
      {
        headerName: "W",
        field: "Width",
        width: 50,
      },
      {
        headerName: "H",
        field: "Height",
        width: 50,
      },
      {
        headerName: "lbs",
        field: "Pounds",
        width: 50,
      },
      {
        headerName: "oz",
        field: "Ounces",
        width: 50,
      },
    ];

    this.frameworkComponents = {
      dateCell: date,
    };
  },
  mounted() {
    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
    this.$store
      .dispatch("orders/fetchTracking", this.order.SourceOrderNumber)
      .then(() => {
        this.rowData = this.order.tracking?.data?.filter(
          (tracking) => tracking.IsVoid != "Y",
        );
      });
  },
  created() {
    ModalBus.$on("order:tracking:deleted", this.refreshOrder);
  },
  methods: {
    onFirstDataRendered(params) {
      params.api.sizeColumnsToFit();
    },
    onGridReady() {},
    orderLink(params) {
      if (params.data.ButtonPrefix == null) return params.data.TrackingID;
      if (params.data.TrackingID == null || params.data.TrackingID == "")
        return null;
      return `<a href="${params.data.ButtonPrefix}${params.value}${
        params.data.ButtonSuffix ?? ""
      }" class="link" target="_blank">${params.data.TrackingID}</a>`;
    },
    toggleSlideover(component, title, properties = {}) {
      if (component) {
        this.slideoverComponent = component;
        this.slideoverTitle = title;
        this.slideoverProperties = properties;
      }

      setTimeout(() => {
        this.slideoverVisible = !this.slideoverVisible;
      }, 3);
    },
    refreshOrder() {
      this.$router.go(0);
    },
    trackingSelectionChanged() {
      let rows = this.gridOptions.api.getSelectedRows();
      if (rows.length !== 1) {
        this.selectedTracking = {};
        return;
      }
      this.selectedTracking = rows[0];
    },
    confirmDeleteTracking() {
      ModalBus.$emit("open", {
        component: DeleteTrackingConfirmModal,
        closeOnOutsideClick: false,
        props: {
          tracking: this.selectedTracking,
        },
      });
    },
  },
};
</script>
<style lang="scss">
@import "../../../../node_modules/ag-grid-community/dist/styles/ag-grid.css";
@import "../../../../node_modules/ag-grid-community/dist/styles/ag-theme-balham.css";

.link {
  @apply ml-0 text-green-600 transition duration-150 ease-in-out;
}

.link:hover {
  @apply text-green-500 underline;
}
</style>
